import { ReactElement, useEffect, useState } from 'react';

import { Box, Button, Group, Loader, Pill, Stack, Text, Tooltip } from '@mantine/core';
import { IconCheck } from '@tabler/icons-react';
import Select, {
  ActionMeta,
  components,
  MultiValue,
  OptionProps,
  StylesConfig,
  ValueContainerProps
} from 'react-select';

import InfoIcon from '@/assets/icons/InfoIcon';
import { plum } from '@/styles/colors';

import { LabelAndValue } from '@/types/custom';

import InputLabel from './InputLabel';

export interface MultiSelectDropdownProps {
  defaultValues?: LabelAndValue[];
  options: LabelAndValue[];
  onChange?: (values: LabelAndValue[]) => void;
  maxItemsToDisplay?: number;
  label?: string;
  maxMenuHeight?: number;
  placeholder?: string;
  isLoading?: boolean;
  showTooltip?: boolean;
  tooltipMessage?: string;
  infoMessage?: string | null;
}

// Custom styles for the select component
const selectStyles: StylesConfig<LabelAndValue, true> = {
  control: (styles, { isFocused }) => ({
    ...styles,
    backgroundColor: 'white',
    borderColor: isFocused ? plum[6] : plum[3],
    '&:hover': {
      borderColor: plum[5]
    },
    cursor: 'pointer',
    minHeight: '35px'
  }),
  option: (styles, { isSelected, isFocused }) => ({
    ...styles,
    backgroundColor: isSelected ? plum[5] : isFocused ? plum[2] : 'transparent',
    color: isSelected ? 'white' : plum[8],
    cursor: 'pointer',
    '&:hover': {
      backgroundColor: isSelected ? plum[6] : plum[2]
    }
  }),
  multiValue: (styles) => ({
    ...styles,
    backgroundColor: plum[2]
  }),
  multiValueLabel: (styles) => ({
    ...styles,
    color: 'plum[9]'
  }),
  multiValueRemove: (styles) => ({
    ...styles,
    color: plum[6],
    ':hover': {
      backgroundColor: plum[5],
      color: 'white'
    }
  }),
  clearIndicator: (styles) => ({
    ...styles,
    color: plum[6],
    ':hover': {
      color: plum[7]
    }
  }),
  dropdownIndicator: (styles) => ({
    ...styles,
    color: plum[6],
    ':hover': {
      color: plum[7]
    }
  }),
  indicatorSeparator: (styles) => ({
    ...styles,
    backgroundColor: plum[4]
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 9999
  })
};

// Option Component with Tooltip
const Option = (props: OptionProps<LabelAndValue, true>) => {
  const { showTooltip, tooltipMessage } = props.selectProps as any;
  const hasDescription = !!props.data.desc;

  // Get the currently selected values to check accounts
  const selectedValues = props.getValue() as LabelAndValue[];

  // Check if there are any selected options from a different account
  const hasDifferentAccountSelected =
    selectedValues.length > 0 &&
    selectedValues.some((selected) => {
      // Find the full option data for the selected value
      const selectedOption = (props.options as LabelAndValue[]).find(
        (opt) => opt.value === selected.value
      );
      // Compare accounts - show tooltip only if this option has a different account
      return selectedOption?.desc !== props.data.desc;
    });

  const optionContent = (
    <components.Option {...props}>
      <Group justify="space-between" w="100%">
        <Stack gap={4}>
          <Text size="sm">{props.data.label}</Text>
          {hasDescription && (
            <Text size="xs" opacity={0.5}>
              {props.data.desc}
            </Text>
          )}
        </Stack>
        {props.isSelected && (
          <IconCheck
            data-testid="icon-check"
            size={14}
            color={props.isSelected ? 'white' : plum[6]}
          />
        )}
      </Group>
    </components.Option>
  );

  // Show tooltip only if:
  // 1. Tooltip is enabled
  // 2. Has description (account name)
  // 3. Option is NOT already selected
  // 4. There are selected options from a DIFFERENT account
  if (
    showTooltip &&
    hasDescription &&
    tooltipMessage &&
    !props.isSelected &&
    hasDifferentAccountSelected
  ) {
    return (
      <Tooltip
        label={tooltipMessage}
        position="right"
        withArrow
        multiline
        w={220}
        styles={{
          tooltip: {
            backgroundColor: plum[9],
            color: 'white'
          }
        }}>
        <div>{optionContent}</div>
      </Tooltip>
    );
  }

  return optionContent;
};

// Value Container Component
const ValueContainer = ({ children, ...props }: ValueContainerProps<LabelAndValue, true>) => {
  const { maxItemsToDisplay = 2, label = 'Options' } = props.selectProps as any;
  const [values, input] = children as ReactElement[];
  const selectedCount = props.getValue().length;
  return (
    <components.ValueContainer {...props}>
      {selectedCount > maxItemsToDisplay ? (
        <Pill
          styles={{
            root: {
              borderRadius: 4,
              backgroundColor: plum[2],
              color: plum[7]
            }
          }}>{`${selectedCount} ${label} Selected`}</Pill>
      ) : selectedCount > 0 ? (
        <Box maw="95%">{values}</Box>
      ) : (
        values
      )}
      {input}
    </components.ValueContainer>
  );
};

// Main Component
const MultiSelectDropdown = ({
  defaultValues = [],
  options,
  onChange,
  maxItemsToDisplay = 2,
  label = 'Options',
  maxMenuHeight = 220,
  placeholder = 'Select one or more..',
  isLoading,
  showTooltip = false,
  tooltipMessage = '',
  infoMessage
}: MultiSelectDropdownProps) => {
  const [selectedOptions, setSelectedOptions] = useState<LabelAndValue[]>(defaultValues);

  useEffect(() => {
    setSelectedOptions(defaultValues);
  }, [defaultValues]);

  const handleChange = (
    newValue: MultiValue<LabelAndValue>,
    actionMeta: ActionMeta<LabelAndValue>
  ) => {
    const values = newValue as LabelAndValue[];
    setSelectedOptions(values);

    // Call onChange immediately on every selection/deselection
    onChange?.(values);
  };

  const handleSelectAll = () => {
    // If there are selected options, only select from the same account
    if (selectedOptions.length > 0) {
      // Get the account of the first selected option
      const firstSelectedOption = options.find((opt) => opt.value === selectedOptions[0].value);
      const activeAccount = firstSelectedOption?.desc || 'No Account';

      // Filter options to only include those from the same account
      const optionsFromSameAccount = options.filter(
        (opt) => (opt.desc || 'No Account') === activeAccount
      );

      setSelectedOptions(optionsFromSameAccount);
      onChange?.(optionsFromSameAccount);
    } else {
      // If no options are selected, select all
      setSelectedOptions(options);
      onChange?.(options);
    }
  };

  const handleDeselectAll = () => {
    setSelectedOptions([]);
    onChange?.([]);
  };

  // Determine if all clinics from the active account are selected
  const isAllSelected = (() => {
    if (selectedOptions.length === 0) {
      return options.length === 0;
    }

    // Get the active account from selected options
    const firstSelectedOption = options.find((opt) => opt.value === selectedOptions[0].value);
    const activeAccount = firstSelectedOption?.desc || 'No Account';

    // Get all options from the same account
    const optionsFromSameAccount = options.filter(
      (opt) => (opt.desc || 'No Account') === activeAccount
    );

    // Check if all options from the active account are selected
    return (
      optionsFromSameAccount.length > 0 &&
      optionsFromSameAccount.every((opt) =>
        selectedOptions.some((selected) => selected.value === opt.value)
      )
    );
  })();

  return (
    <Stack gap={2} w={'100%'}>
      <Group w={'100%'} justify="space-between">
        <Box>
          <InputLabel label={label} name={label} labelColor="#A27E8A" />{' '}
          {isLoading && <Loader ml={5} size={14} color={plum[5]} pos={'relative'} top={3} />}
        </Box>
        <Button
          size="xs"
          variant="subtle"
          onClick={isAllSelected ? handleDeselectAll : handleSelectAll}
          disabled={options.length === 0}
          color="plum">
          {isAllSelected ? 'Deselect All' : 'Select All'}
        </Button>
      </Group>

      {infoMessage && (
        <Group display="flex" align="flex-start" mb={2} gap="0">
          <InfoIcon />
          <Text size="xs" c="dimmed" ml="4" mt="-1">
            {infoMessage}
          </Text>
        </Group>
      )}

      <Select<LabelAndValue, true>
        isMulti
        name={label}
        placeholder={placeholder}
        defaultValue={defaultValues}
        value={selectedOptions}
        options={options}
        onChange={handleChange}
        components={{
          Option,
          ValueContainer
        }}
        isDisabled={isLoading}
        maxMenuHeight={maxMenuHeight}
        menuPortalTarget={document.body}
        styles={selectStyles}
        closeMenuOnSelect={false}
        hideSelectedOptions={false}
        theme={(theme) => ({
          ...theme,
          colors: {
            ...theme.colors,
            primary: plum[6],
            primary75: plum[5],
            primary50: plum[4],
            primary25: plum[2]
          }
        })}
        {...({
          showTooltip,
          tooltipMessage,
          maxItemsToDisplay,
          label
        } as any)}
      />
    </Stack>
  );
};

export default MultiSelectDropdown;
